import { ArrowRightCircle } from 'lucide-react'
import { useGetGoogleReviews } from '@cannect/services/resources/reviews'
import { testimonialsData, type Testimonial } from './testimonialsData'
import { vidaMelhorImagePath } from './vidaMelhorImagePath'

interface TestimonialsProps {
  showPromoCard?: boolean
  useGoogleReviews?: boolean
  enableHorizontalScroll?: boolean
}

export default function Testimonials({
  showPromoCard = true,
  useGoogleReviews = true,
  enableHorizontalScroll = false
}: TestimonialsProps) {
  const { data: googleReviewsData, isLoading, isError } = useGetGoogleReviews()

  // Usa Google Reviews se disponível, senão fallback para dados estáticos
  const shouldUseGoogle = useGoogleReviews && googleReviewsData && !isError
  const googleReviews = googleReviewsData?.fiveStarReviews?.reviews || []

  // Mapeia Google Reviews para o formato do componente
  const mappedGoogleReviews: Testimonial[] = googleReviews.map((review) => ({
    name: review.author,
    image: review.authorPhoto,
    age: 0, // Google não fornece idade
    quote: review.text,
    date: review.relativeTime
  }))

  // Seleciona quais reviews exibir
  let testimonialsToShow: Testimonial[] = []
  if (shouldUseGoogle) {
    testimonialsToShow = showPromoCard ? mappedGoogleReviews.slice(0, 3) : mappedGoogleReviews
  } else {
    testimonialsToShow = showPromoCard ? testimonialsData.slice(0, 3) : testimonialsData
  }

  const redirectToWhatsApp = () => {
    window.open('https://whts.co/Cannect', '_blank')
  }

  // Loading state
  if (isLoading && useGoogleReviews) {
    return (
      <section className="flex w-full flex-col items-start px-4 py-10 md:px-0">
        <h2 className="mb-6 text-2xl font-normal leading-snug md:mb-6">Histórias de quem já confia na Cannect</h2>
        <div
          className={`flex w-full gap-6 ${
            enableHorizontalScroll
              ? 'flex-row overflow-x-auto pb-4 md:flex-nowrap md:items-stretch'
              : 'flex-col md:flex-row md:flex-nowrap md:items-stretch md:overflow-hidden'
          }`}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`flex animate-pulse flex-col items-start gap-4 rounded-lg bg-gray-100 p-6 ${
                enableHorizontalScroll ? 'min-w-[280px] flex-shrink-0 md:w-80' : 'w-full md:w-80 md:flex-shrink-0'
              }`}>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-full bg-gray-300" />
                <div className="flex flex-col gap-2">
                  <div className="h-4 w-32 rounded bg-gray-300" />
                  <div className="h-3 w-20 rounded bg-gray-300" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-300" />
                <div className="h-3 w-full rounded bg-gray-300" />
                <div className="h-3 w-3/4 rounded bg-gray-300" />
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className="flex w-full flex-col items-start gap-8 px-4 py-10 md:px-0">
      <h2 className="text-2xl font-normal leading-snug">Histórias de quem já confia na Cannect</h2>

      <div
        className={`flex w-full gap-6 ${
          enableHorizontalScroll
            ? 'flex-row overflow-x-auto pb-4 md:flex-nowrap md:items-stretch'
            : 'flex-col md:flex-row md:flex-nowrap md:items-stretch md:overflow-hidden'
        }`}>
        {testimonialsToShow.map((testimonial: Testimonial, index: number) => (
          <div
            key={index}
            className={`flex flex-col items-start gap-4 rounded-lg bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${
              enableHorizontalScroll
                ? 'min-w-[280px] max-w-[280px] flex-shrink-0 md:w-80 md:max-w-none'
                : 'w-full md:w-80 md:flex-shrink-0'
            }`}>
            <div className="flex items-center gap-3">
              <img
                src={testimonial.image}
                alt={testimonial.name}
                className="h-14 w-14 rounded-full object-cover"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).src = 'https://via.placeholder.com/56'
                }}
              />
              <div className="flex min-w-0 flex-col">
                <p className="break-words font-semibold leading-snug text-neutral-900">{testimonial.name}</p>
                {testimonial.age > 0 && (
                  <p className="break-words text-sm leading-tight text-neutral-700">{testimonial.age} anos</p>
                )}
              </div>
            </div>

            <p className="overflow-wrap-anywhere line-clamp-4 break-words font-ebGaramond text-sm leading-relaxed text-neutral-900">
              &ldquo;{testimonial.quote}&rdquo;
            </p>

            {shouldUseGoogle && (testimonial as any).date && (
              <p className="mt-auto text-xs text-neutral-500">{(testimonial as any).date}</p>
            )}
          </div>
        ))}

        {showPromoCard && (
          <div className="hidden w-80 flex-shrink-0 md:block">
            <div className="flex h-full w-full items-stretch overflow-hidden rounded-lg bg-primary-100 shadow-sm">
              {/* Imagem à esquerda */}
              <div className="w-[96px] flex-shrink-0">
                <img src={vidaMelhorImagePath} alt="Comece uma vida melhor" className="h-full w-full object-cover" />
              </div>

              {/* Conteúdo à direita */}
              <div className="flex flex-1 flex-col justify-between p-4">
                {/* Título no topo */}
                <p className="text-sm font-semibold leading-snug text-neutral-800">
                  Comece uma <br /> vida melhor
                </p>

                {/* Texto do meio */}
                <p className="my-auto text-sm leading-relaxed text-neutral-800">Fale com nossa equipe de atendimento</p>

                {/* Botão no rodapé */}
                <button
                  className="mt-auto flex items-center gap-1 border-0 bg-transparent text-xs font-semibold text-primary-800 transition-colors hover:underline"
                  onClick={redirectToWhatsApp}>
                  fale conosco <ArrowRightCircle size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
